import { useEffect, useState } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { useAuthStatus } from '../store/auth/useAuthStatus';
import useUsersFacade from '../store/profile/useUsersFacade';
import { setCookie } from '../utils/cookies';
import { useConfig } from '../Hooks/useConfig';

function PrivateRouteAndStepper({ component: Component, ...rest }) {
  const [authChecked, setAuthChecked] = useState(false);
  const { loginState } = useAuthStatus();
  const { userInfo } = useUsersFacade();
  const location = useLocation();
  const { baseDomain } = useConfig();

  // Determine if we're in playground mode (not in panel)
  const subdomain = window.location.hostname.split('.')[0] || '';
  const isPanel = subdomain.includes('panel');
  const isPlayground = !isPanel;

  // Update loading state based on user data
  useEffect(() => {
    if (!loginState) {
      // If not logged in, we can immediately determine routing
      setAuthChecked(true);
    } else if (userInfo) {
      // Only consider auth checked when we have user info
      setAuthChecked(true);
    }
    // Keep authChecked as false while waiting for userInfo
  }, [loginState, userInfo]);

  // Check if user needs to be redirected to stepper
  const needsStepperRedirect = () => {
    // Only redirect if the user is logged in and has valid userInfo
    if (loginState && userInfo) {
      // First check for email verification
      if (userInfo.email_verified_at === null) {
        setCookie('showStepper', 'false', 34560000, baseDomain);
        return false; // Don't redirect to stepper if email is not verified yet
      }

      // If email is verified, check if user is a first-time user
      // And make sure we're not already at the stepper page (to avoid redirection loops)
      if (userInfo.show_stepper && location.pathname !== '/stepper') {
        setCookie('showStepper', 'true', 34560000, baseDomain);
      }
      return userInfo.show_stepper && location.pathname !== '/stepper';
    }

    setCookie('showStepper', 'false', 34560000, baseDomain);
    return false;
  };

  // Check if user needs to be redirected to stepper
  const hasEmailBeenVerified = () => {
    if (loginState && userInfo) {
      if (userInfo.email_verified_at === null) {
        setCookie('emailNotConfirmed', 'true', 34560000, baseDomain);
        return true;
      }
    }

    setCookie('emailNotConfirmed', 'false', 34560000, baseDomain);
    return false;
  };

  // Show absolutely nothing until we've checked auth status
  if (!authChecked) {
    return null;
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        // If user is not logged in AND we're not in playground, redirect to login
        if (!loginState && !isPlayground) {
          return (
            <Redirect
              to={{
                pathname: '/login',
                search: location.search,
                state: { from: location.pathname },
              }}
            />
          );
        }

        if (loginState && hasEmailBeenVerified()) {
          return (
            <Redirect
              to={{
                pathname: '/confirm-email',
                search: location.search,
                state: { from: location.pathname },
              }}
            />
          );
        }

        // If user is logged in but needs to go through stepper, redirect to stepper
        if (loginState && needsStepperRedirect()) {
          return (
            <Redirect
              to={{
                pathname: '/stepper',
                search: location.search,
                state: { from: location.pathname },
              }}
            />
          );
        }

        // User is either logged in OR we're in playground mode, render the component
        return (
          <Component
            getUserInfo={rest?.getUserInfo}
            {...props}
            search={location.search}
          />
        );
      }}
    />
  );
}

export default PrivateRouteAndStepper;
